import {
  ArrowDown, ArrowRight, BookOpen, Check, ChevronDown, Code2, Compass, Cpu, Dumbbell,
  Eye, Handshake, Info, Languages, Lock, MapPin, Menu, MessageSquare, Music, Navigation,
  Palette, Rocket, Search, Send, ShieldCheck, Sparkles, Target, Trophy, UserCheck, UserPlus, Users, X,
} from 'lucide-react'
import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { HeroDiscoveryMap } from '../components/home/HeroDiscoveryMap'
import { Logo } from '../components/Logo'
import { usePageMeta } from '../hooks/usePageMeta'

/*
 * Landing page, rebuilt to match the yellow-dot.ai.studio design.
 *
 * Palette, type and copy are taken from that design directly: #FFBE1A brand yellow on #0A192F
 * navy over #FBF9F4 paper, Plus Jakarta Sans throughout with Caveat for the handwritten asides.
 * Photography is the same Unsplash set the reference uses, served from Unsplash's own CDN.
 */

/* ------------------------------------------------------------------ atoms */

function Spark({ size = 16, className = '' }: { size?: number; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden="true">
      <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" fill="currentColor" />
    </svg>
  )
}

/** Section label: uppercase, letterspaced, with the dot mark. */
function Eyebrow({ children, tone = 'light' }: { children: React.ReactNode; tone?: 'light' | 'dark' }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.16em] ${
        tone === 'dark'
          ? 'bg-white/10 text-dot-400 ring-1 ring-white/15'
          : 'bg-dot-400/15 text-dot-700 ring-1 ring-dot-400/30'
      }`}
    >
      <Spark size={11} className="text-dot-400" />
      {children}
    </span>
  )
}

/** Headline word sitting on a solid yellow marker bar, as in the reference hero. */
function Marked({ children }: { children: React.ReactNode }) {
  return (
    <span className="relative inline-block">
      <span className="relative z-10">{children}</span>
      <span className="absolute bottom-1.5 left-0 -z-0 h-3 w-full rounded-sm bg-dot-400 sm:h-4" />
    </span>
  )
}

function SectionHead({
  eyebrow, title, marked, sub, tone = 'light',
}: {
  eyebrow: string
  title: string
  marked?: string
  sub?: string
  tone?: 'light' | 'dark'
}) {
  const dark = tone === 'dark'
  return (
    <div className="mx-auto mb-10 max-w-2xl text-center sm:mb-14">
      <Eyebrow tone={tone}>{eyebrow}</Eyebrow>
      <h2 className={`mt-5 font-sans text-3xl font-extrabold leading-[1.15] tracking-tight sm:text-4xl ${dark ? 'text-white' : 'text-deep-900'}`}>
        {title}{marked ? <> <Marked>{marked}</Marked></> : null}
      </h2>
      {sub ? <p className={`mt-5 text-base leading-relaxed ${dark ? 'text-white/65' : 'text-slate-600'}`}>{sub}</p> : null}
    </div>
  )
}

/* ------------------------------------------------------------------- data */

const IMG = {
  learnerCard: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=800&q=80',
  educatorCard: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80',
  educatorPortrait: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80',
  studentPortrait: 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?auto=format&fit=crop&w=800&q=80',
  avatarA: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=150&q=80',
  avatarB: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=150&q=80',
  avatarC: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80',
}

const CATEGORIES = [
  {
    title: 'Academics', icon: <BookOpen size={18} />,
    description: 'School subjects, boards, mathematics, sciences & foundational learning.',
    image: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=600&q=80',
    tags: ['CBSE', 'ICSE', 'Maths', 'Physics', 'Commerce'],
    accent: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  },
  {
    title: 'Competitive Exams', icon: <Trophy size={18} />,
    description: 'Focused preparation and mentoring for JEE, NEET, UPSC and beyond.',
    image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=600&q=80',
    tags: ['JEE', 'NEET', 'UPSC', 'CUET', 'Foundations'],
    accent: 'border-amber-200 bg-amber-50 text-amber-800',
  },
  {
    title: 'Music', icon: <Music size={18} />,
    description: 'Instruments, vocal training, music theory, performance & studio basics.',
    image: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=600&q=80',
    tags: ['Guitar', 'Keyboard', 'Vocal', 'Drums', 'Violin'],
    accent: 'border-orange-200 bg-orange-50 text-orange-700',
  },
  {
    title: 'Health, Sports & Fitness', icon: <Dumbbell size={18} />,
    description: 'One-on-one coaching, athletic development, yoga, fitness & training.',
    image: 'https://images.unsplash.com/photo-1517838277536-f5f99be501cd?auto=format&fit=crop&w=600&q=80',
    tags: ['Badminton', 'Cricket', 'Yoga', 'Martial Arts', 'Fitness'],
    accent: 'border-blue-200 bg-blue-50 text-blue-700',
  },
  {
    title: 'Arts & Creativity', icon: <Palette size={18} />,
    description: 'Drawing, sketching, painting, classical dance, drama, and handicrafts.',
    image: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=600&q=80',
    tags: ['Fine Arts', 'Kathak', 'Contemporary', 'Theatre', 'Crafts'],
    accent: 'border-purple-200 bg-purple-50 text-purple-700',
  },
  {
    title: 'Skills & Technology', icon: <Code2 size={18} />,
    description: 'Programming, web design, financial literacy, UI/UX & practical skills.',
    image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=600&q=80',
    tags: ['Python', 'Web Dev', 'UI/UX Design', 'Data', 'Finance'],
    accent: 'border-teal-200 bg-teal-50 text-teal-700',
  },
  {
    title: 'AI Fluency', icon: <Cpu size={18} />,
    description: 'Future-ready skills, prompt engineering, generative AI & modern tools.',
    image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=600&q=80',
    tags: ['Generative AI', 'Prompting', 'Automation', 'Future Skills'],
    accent: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  },
  {
    title: 'Languages', icon: <Languages size={18} />,
    description: 'Spoken English, German, French, regional Indian languages & fluency.',
    image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=600&q=80',
    tags: ['Spoken English', 'French', 'German', 'Spanish', 'Sanskrit'],
    accent: 'border-rose-200 bg-rose-50 text-rose-700',
  },
  {
    title: 'Entrepreneurship', icon: <Rocket size={18} />,
    description: 'Co-founders, mentors, advisors and early-stage funding, right in your neighbourhood.',
    image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=600&q=80',
    tags: ['Co-founders', 'Mentors', 'Investors', 'Idea Validation'],
    accent: 'border-yellow-200 bg-yellow-50 text-yellow-700',
  },
]

const CHALLENGES = [
  { icon: <Compass size={18} />, t: 'Too many options', d: 'Learning works better when the person, teaching style, need and location fit seamlessly.' },
  { icon: <Search size={18} />, t: 'Hard to find locally', d: 'Great educators and learning opportunities may be close by, but difficult to discover.' },
  { icon: <Users size={18} />, t: "Word of mouth isn't enough", d: 'Educators often depend only on personal networks, missing learners looking for what they teach.' },
  { icon: <MapPin size={18} />, t: 'Scattered sources', d: 'Learning options are scattered across different places with no single local directory.' },
]

const REQUESTS = [
  { quote: 'Looking for a Class 10 Maths tutor near Indirapuram.', role: 'Learner', location: 'Ghaziabad', category: 'Academics', distance: 'Within 1.5 km', avatar: IMG.avatarA },
  { quote: 'Need a guitar teacher for weekends in Vaishali.', role: 'Learner', location: 'Ghaziabad', category: 'Music & Instruments', distance: 'Within 2.0 km', avatar: IMG.avatarB },
  { quote: 'Looking for guidance for UPSC preparation nearby.', role: 'Learner', location: 'Ghaziabad', category: 'Competitive Exams', distance: 'Within 3.0 km', avatar: IMG.avatarC },
]

const STEPS = [
  { icon: <Search size={20} />, t: 'Search', d: 'Tell us what you want to learn and how far you are willing to go.' },
  { icon: <Eye size={20} />, t: 'Discover', d: 'See tutors, coaches, trainers and mentors right around your neighbourhood.' },
  { icon: <MessageSquare size={20} />, t: 'Connect', d: 'Send a connection request with mutual privacy protected.' },
  { icon: <UserCheck size={20} />, t: 'Learn', d: 'Connect and take the learning journey forward on your terms.' },
]

/** Pins on the discovery map. x/y are percentages of the map frame. */
const MAP_PINS = [
  { title: 'Maths Tutor', category: 'Academics', distance: '1.2 km away', location: 'Indirapuram', icon: <BookOpen size={14} />, x: 34, y: 32, highlight: true },
  { title: 'Guitar Coach', category: 'Music', distance: '2.1 km away', location: 'Vaishali', icon: <Music size={14} />, x: 68, y: 26, highlight: false },
  { title: 'Football Trainer', category: 'Health, Sports & Fitness', distance: '0.8 km away', location: 'Kaushambi', icon: <Trophy size={14} />, x: 24, y: 68, highlight: true },
  { title: 'Spoken English', category: 'Languages', distance: '1.5 km away', location: 'Ahinsa Khand', icon: <Languages size={14} />, x: 52, y: 74, highlight: false },
  { title: 'Coding Mentor', category: 'Skills & Tech', distance: '2.4 km away', location: 'DC Chowk', icon: <Code2 size={14} />, x: 78, y: 60, highlight: false },
]

const NAV_LINKS = [
  { href: '#how', label: 'How It Works' },
  { href: '#for-learners', label: 'For Learners' },
  { href: '#for-educators', label: 'For Educators' },
  { href: '#why', label: 'Why YellowDots' },
]

const PLACES = [
  { name: 'Indirapuram', x: 38, y: 8 },
  { name: 'Vaishali', x: 74, y: 6 },
  { name: 'Kaushambi', x: 16, y: 16 },
  { name: 'DC Chowk', x: 82, y: 86 },
]

/* -------------------------------------------------------------- the page */

export function HomePage() {
  usePageMeta(
    'Find a tutor or a coach or an instructor or a job near you',
    'YellowDots connects learners with trusted tutors, coaches, trainers and educators nearby. Search free, connect on your terms, and find the right learning fit in your neighbourhood.',
  )
  const { token, loading } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  if (!loading && token) return <Navigate to="/dashboard" replace />

  return (
    <div className="min-h-screen bg-cream-50 font-sans text-deep-900">
      {/* ---------------------------------------------------------- nav */}
      <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-cream-50/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5">
          {/* The landing page had its own hand-built lockup. Replaced by the same brand file the
              rest of the app uses, so the two cannot drift apart again. */}
          <Link to="/" className="flex min-w-0 items-center" aria-label="YellowDots home">
            <Logo />
          </Link>
          <nav className="hidden items-center gap-8 text-sm font-bold text-deep-900 lg:flex">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} className="transition hover:text-dot-600">{l.label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Organisations have a portal of their own - see AggregatorPortalAuthPage. One door: its
                sign-up page links to sign-in for organisations and coordinators already there. */}
            <Link
              to="/aggregator-portal/register"
              className="hidden rounded-full border-2 border-deep-900/25 px-4 py-2 text-xs font-bold tracking-wide text-deep-900 transition hover:border-deep-900/50 md:inline-flex"
            >
              JOIN AS PARTNER
            </Link>
            <Link to="/login" className="hidden text-sm font-bold text-deep-900 transition hover:text-dot-600 sm:inline-flex">Login</Link>
            <Link to="/register" className="btn-dot !px-4 !py-2.5 !text-[11px] !tracking-wide sm:!px-6 sm:!py-3 sm:!text-xs">
              JOIN THE NETWORK <ArrowRight size={15} />
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-slate-200 bg-white text-deep-900 lg:hidden"
            >
              {menuOpen ? <X size={17} /> : <Menu size={17} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav className="border-t border-slate-200/70 bg-cream-50 px-4 py-3 text-sm font-bold text-deep-900 lg:hidden">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                className="block rounded-lg px-2 py-2.5 transition hover:bg-dot-400/15 hover:text-dot-700"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              className="block rounded-lg px-2 py-2.5 transition hover:bg-dot-400/15 hover:text-dot-700 sm:hidden"
            >
              Login
            </Link>
            <Link
              to="/aggregator-portal/register"
              onClick={() => setMenuOpen(false)}
              className="block rounded-lg px-2 py-2.5 transition hover:bg-dot-400/15 hover:text-dot-700 md:hidden"
            >
              Join as Partner
            </Link>
          </nav>
        )}
      </header>

      {/* --------------------------------------------------------- hero */}
      <section className="relative overflow-hidden bg-dot-hero">
        {/* pt-0 below lg: the map sits first there now (order-1) and should meet the header
            directly, like a native map view. Only the desktop layout - text first - still wants
            the pt-10 breathing room above the eyebrow. */}
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 pb-20 pt-0 lg:grid-cols-12 lg:pt-10">
          <div className="order-2 min-w-0 lg:order-1 lg:col-span-6">
            <Eyebrow>Your local connection to learning</Eyebrow>

            <h1 className="mt-6 font-sans text-4xl font-extrabold leading-[1.12] tracking-tight text-deep-900 sm:text-5xl">
              Where keen learners and trusted coaches find each other. <Marked>Nearby.</Marked>
            </h1>

            <h2 className="mt-6 max-w-lg font-sans text-lg font-bold text-deep-900/90 sm:text-xl">
              Find the right learning connection — or make your expertise discoverable to learners nearby.
            </h2>

            <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">
              YellowDots connects keen learners with trusted coaches, tutors, trainers and educators
              in their neighbourhood — making it easier to discover the right person to learn from
              and easier for great educators to be found.
            </p>

            <p className="mt-5 rotate-[-2deg] font-hand text-2xl font-bold text-dot-600 md:text-3xl">
              &ldquo;Great learning could be closer than you think.&rdquo;
            </p>

            <div className="mt-7 flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
              <Link to="/register" className="btn-dot !px-8 !py-4 text-sm shadow-lg">
                JOIN THE NETWORK <ArrowRight size={17} />
              </Link>
              <a href="#how" className="btn-outline !px-8 !py-4 text-sm">
                SEE HOW IT WORKS <ArrowDown size={16} />
              </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 border-t border-slate-200/80 pt-6 text-xs font-semibold text-slate-600">
              {[
                { c: 'bg-dot-400', t: 'Hyperlocal Discovery' },
                { c: 'bg-emerald-500', t: '100% Consent-Led' },
                { c: 'bg-blue-500', t: 'Real Verified People' },
              ].map((x) => (
                <div key={x.t} className="flex items-center gap-1.5">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${x.c}`} /> {x.t}
                </div>
              ))}
            </div>
          </div>

          {/* The map itself, as the hero.

              This was a portrait ringed by category chips. The chips claimed "seven kinds of thing
              are near you"; the map shows the product making good on it - a neighbourhood, dots on
              it, and one result with the Connect button that is the whole proposition. */}
          {/* lg:mt matches the Eyebrow's own rendered height plus the h1's mt-6, so the map's top
              edge lines up with "Where keen learners and" rather than with the eyebrow above it.
              order-1 puts it first on mobile - the proof (a real map) before the pitch, in the
              first fold - while lg:order-2 keeps it on the right at desktop, beside the text.
              -mx-4 cancels the grid container's own px-4 so the card can bleed edge-to-edge on a
              phone; sm:mx-0 gives it back once there is room to spare. */}
          <div className="relative order-1 flex min-w-0 items-center justify-center -mx-4 px-0 pb-8 pt-0 sm:mx-0 sm:px-8 sm:pb-12 lg:order-2 lg:col-span-6 lg:px-4 lg:pt-0 lg:mt-[52px]">
            <Send size={22} className="absolute right-2 top-0 hidden -rotate-12 text-slate-400 sm:block" aria-hidden="true" />
            <HeroDiscoveryMap />
          </div>
        </div>

        {/* closing aside, centred under the whole hero */}
        <div className="mx-auto max-w-7xl border-t border-slate-200/80 px-4 py-10">
          <p className="text-center font-hand text-2xl font-bold text-deep-900 md:text-3xl">
            &ldquo;Whatever you want to learn, someone nearby may be able to help.&rdquo;
          </p>
        </div>
      </section>

      {/* ------------------------------------------- what brings you here */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
        <SectionHead
          eyebrow="Choose your pathway"
          title="What brings you to Yellow"
          marked="Dot?"
          sub="Whether you want to learn something new or share your expertise, local connection starts here."
        />
        <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2">
          {/* Learner */}
          <div className="group soft overflow-hidden p-6">
            <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-slate-700">
              For Learners
            </span>
            <div className="relative mb-6 mt-5 h-48 overflow-hidden rounded-2xl border border-slate-200 sm:h-56">
              <img src={IMG.learnerCard} alt="Learner learning with local coach"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <h3 className="font-sans text-2xl font-extrabold">I&rsquo;m looking to learn</h3>
            <ul className="mt-5 space-y-2.5 text-sm font-medium text-slate-700">
              {[
                'Discover tutors, coaches and trainers near you',
                'Compare profiles, expertise and what fits your need',
                'Connect directly on your terms with total privacy',
                'Explore options freely before you commit',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <Check size={16} className="mt-0.5 shrink-0 text-dot-600" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/register" className="btn-dot mt-7 w-full">Register as a learner <ArrowRight size={17} /></Link>
          </div>

          {/* Educator */}
          <div className="group soft overflow-hidden bg-deep-900 p-6 text-white">
            <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
              For Teachers &amp; Coaches
            </span>
            <div className="relative mb-6 mt-5 h-48 overflow-hidden rounded-2xl border border-slate-700 sm:h-56">
              <img src={IMG.educatorCard} alt="Passionate educator teaching"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <h3 className="font-sans text-2xl font-extrabold">I teach, train or coach</h3>
            <ul className="mt-5 space-y-2.5 text-sm font-medium text-white/85">
              {[
                'Make your expertise discoverable to learners nearby',
                'Build your neighbourhood reputation & presence',
                'Fill available slots without heavy commissions',
                'Accept only the connections you want',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <Check size={16} className="mt-0.5 shrink-0 text-dot-400" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/register" className="btn-dot mt-7 w-full">Register as an educator <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------- map discovery */}
      <section id="discover" className="border-y border-slate-200/70 bg-white py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <Eyebrow>Map-based local discovery</Eyebrow>
            <h2 className="mt-5 font-sans text-3xl font-extrabold leading-[1.15] tracking-tight sm:text-4xl">
              See learning options around you.
            </h2>
            <p className="mt-4 font-hand text-2xl font-bold text-dot-600">
              &ldquo;Great learning could be closer than you think — and now you can see it.&rdquo;
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Discover tutors, coaches, trainers and educators around your neighbourhood, based on
              what you want to learn and how far you&rsquo;re willing to go.
            </p>
          </div>

          <div className="soft overflow-hidden">
            {/* Filters. Disabled on purpose: discovery is not wired up yet, and controls that
                look live but do nothing are worse than controls that say so. */}
            <div className="grid gap-4 border-b border-slate-200 px-5 py-5 lg:grid-cols-[1fr_1fr_1fr_auto]">
              {[
                { label: 'What do you want to learn?', value: 'All Learning Areas' },
                { label: 'Where?', value: 'Near me (Current Location)' },
                { label: 'Distance', value: 'Up to 5 km (Short drive)' },
              ].map((f) => (
                <label key={f.label} className="block">
                  <span className="mb-1.5 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                    {f.label}
                  </span>
                  <span className="relative block">
                    <select
                      disabled
                      defaultValue={f.value}
                      aria-label={f.label}
                      className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-deep-900 disabled:cursor-not-allowed"
                    >
                      <option>{f.value}</option>
                    </select>
                    <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  </span>
                </label>
              ))}
              <div className="flex items-end">
                <Link to="/register" className="btn-dot !px-6 !py-2.5 w-full !text-xs">
                  EXPLORE NEARBY <ArrowRight size={15} />
                </Link>
              </div>
            </div>

            {/* The pins are absolutely positioned cards of a fixed width, so the frame cannot
                usefully shrink below a tablet. Below that it pans horizontally instead of
                crushing the pins into each other. */}
            <div className="overflow-x-auto">
            <div className="relative aspect-[16/10] w-full min-w-[720px] overflow-hidden bg-[#EEF4FA] sm:aspect-[16/8]">
              {/* Stylised street grid, green space and water - an illustration, not a real map. */}
              <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1000 560" preserveAspectRatio="none" aria-hidden="true">
                <rect width="1000" height="560" fill="#EEF4FA" />
                <rect x="60" y="70" width="190" height="120" rx="10" fill="#E4EEE2" />
                <rect x="820" y="330" width="170" height="150" rx="10" fill="#E4EEE2" />
                <rect x="600" y="430" width="180" height="110" rx="10" fill="#E4EEE2" />
                <path d="M0,140 C220,110 340,180 560,150 C760,124 880,168 1000,140 L1000,186 C880,214 760,170 560,196 C340,226 220,156 0,186 Z" fill="#DCEAF6" />
                {[110, 240, 360, 450, 540].map((y) => (
                  <line key={y} x1="0" y1={y} x2="1000" y2={y} stroke="#FFFFFF" strokeWidth="12" />
                ))}
                {[120, 320, 520, 700, 880].map((x) => (
                  <line key={x} x1={x} y1="0" x2={x} y2="560" stroke="#FFFFFF" strokeWidth="12" />
                ))}
                <line x1="0" y1="240" x2="1000" y2="240" stroke="#CBD5E1" strokeWidth="2" strokeDasharray="10 10" />
                <line x1="520" y1="0" x2="520" y2="560" stroke="#CBD5E1" strokeWidth="2" strokeDasharray="10 10" />
              </svg>

              {PLACES.map((pl) => (
                <span key={pl.name}
                  className="absolute -translate-x-1/2 text-xs font-bold text-slate-500/80"
                  style={{ left: `${pl.x}%`, top: `${pl.y}%` }}>
                  {pl.name}
                </span>
              ))}

              <span className="absolute left-4 top-4 z-20 flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-bold shadow">
                <span className="h-2 w-2 rounded-full bg-dot-400" />
                YellowDots Pins = Available Local Coaches &amp; Tutors
              </span>

              {/* radius rings centred on the learner */}
              {[30, 52, 74].map((pct, i) => (
                <span key={pct}
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-dashed border-dot-600/45"
                  style={{ width: `${pct}%`, aspectRatio: '1', zIndex: 1 }}>
                  {i === 0 ? (
                    <span className="absolute -top-2.5 left-1/2 hidden -translate-x-1/2 bg-[#EEF4FA] px-1.5 text-[10px] font-bold text-dot-700 sm:block">
                      1 km radius
                    </span>
                  ) : null}
                </span>
              ))}

              <span className="absolute left-1/2 top-1/2 z-20 grid h-10 w-10 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-deep-900 text-white shadow-xl">
                <Navigation size={16} className="fill-dot-400 text-dot-400" />
              </span>
              <span className="absolute left-1/2 top-1/2 z-20 mt-7 -translate-x-1/2 whitespace-nowrap rounded-full bg-deep-900 px-3 py-1 text-[10px] font-bold text-white">
                <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-dot-400 align-middle" />
                You Are Here
              </span>

              {MAP_PINS.map((p) => (
                <div key={p.title} className="absolute z-10" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
                  <span className="absolute -left-3.5 -top-9 grid h-7 w-7 place-items-center rounded-full bg-dot-400 shadow-lg ring-4 ring-white">
                    <span className="h-2.5 w-2.5 rounded-full bg-deep-900" />
                  </span>
                  <div className={`w-[168px] rounded-xl border bg-white p-2.5 shadow-lg ${p.highlight ? 'border-dot-400' : 'border-slate-200'}`}>
                    <div className="flex items-start gap-2">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-dot-400/15 text-dot-700">
                        {p.icon}
                      </span>
                      <span className="leading-tight">
                        <span className="block text-xs font-extrabold">{p.title}</span>
                        <span className="block text-[10px] font-semibold text-slate-500">{p.category}</span>
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-1.5">
                      <span className="flex items-center gap-1 text-[10px] font-semibold text-slate-500">
                        <MapPin size={10} className="text-dot-600" /> {p.distance}
                      </span>
                      <span className="text-[9px] font-extrabold tracking-wide text-deep-900">NEARBY →</span>
                    </div>
                  </div>
                </div>
              ))}

              <span className="absolute bottom-3 right-3 z-20 flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-medium text-slate-500 shadow">
                <Info size={11} /> Illustrative sample preview &bull; Discoverable upon launch
              </span>
            </div>
            </div>
            <p className="border-t border-slate-100 px-5 py-2 text-center text-[11px] font-semibold text-slate-400 sm:hidden">
              Swipe the map sideways to see every pin
            </p>

            {/* dark call-to-action bar closing the map card */}
            <div className="flex flex-col items-start justify-between gap-4 bg-deep-900 px-5 py-5 sm:flex-row sm:items-center">
              <div className="flex items-start gap-3">
                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-dot-400" />
                <div>
                  <div className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-dot-400">
                    Local + Visible + Discoverable
                  </div>
                  <p className="mt-1 text-sm text-white/75">
                    Instead of searching the entire internet, see the people who can help you learn
                    right around you.
                  </p>
                </div>
              </div>
              <Link to="/register" className="btn-dot shrink-0 !px-6 !py-3 !text-xs">
                REGISTER TO CONNECT <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------- the problem */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
        <SectionHead
          eyebrow="The learning challenge"
          title="Finding the right learning shouldn’t be this"
          marked="difficult."
          sub="You have the enthusiasm or the expertise. But without a shared local network, finding each other is left to pure chance."
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CHALLENGES.map((c) => (
            <div key={c.t} className="soft-pad">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-dot-400/15 text-dot-700">{c.icon}</span>
              <div className="mt-4 font-extrabold">{c.t}</div>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{c.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-center font-hand text-2xl text-dot-600">
          Great educators shouldn&rsquo;t depend only on word of mouth.
        </p>
      </section>

      {/* -------------------------------------------------- learning needs */}
      <section className="border-y border-slate-200/70 bg-cream-100 py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4">
          <SectionHead
            eyebrow="Flexible learning horizons"
            title="Learning needs aren’t always the"
            marked="same."
            sub="A quick single session for today or a structured, long-term journey that lasts months."
          />
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2">
            <div className="soft-pad">
              <span className="rounded-full bg-dot-400/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-dot-700">Short-term need</span>
              <h3 className="mt-4 font-sans text-2xl font-extrabold">Need something for now?</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Offer doubt clearing, exam prep, or targeted one-off consultations.
              </p>
              <ul className="mt-5 space-y-2.5 text-sm text-slate-700">
                {['A single session', 'Trial / introductory session', 'Weekend / multi-session learning', 'Quick skill support'].map((t) => (
                  <li key={t} className="flex items-start gap-2.5"><Spark size={12} className="mt-1 shrink-0 text-dot-400" /> {t}</li>
                ))}
              </ul>
              <Link to="/register" className="btn-dot mt-6 w-full">Find short-term options</Link>
            </div>

            <div className="soft-pad bg-deep-900 text-white">
              <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-dot-400">Long-term learning</span>
              <h3 className="mt-4 font-sans text-2xl font-extrabold">Looking for a learning journey?</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/70">
                Structured, consistent guidance designed to build deep foundational knowledge,
                steady habit, and measurable mastery over time.
              </p>
              <ul className="mt-5 space-y-2.5 text-sm text-white/85">
                {['Regular classes & structured programmes', 'Regular tutoring', 'Training programmes', 'Ongoing mentorship'].map((t) => (
                  <li key={t} className="flex items-start gap-2.5"><Spark size={12} className="mt-1 shrink-0 text-dot-400" /> {t}</li>
                ))}
              </ul>
              <Link to="/register" className="btn-dot mt-6 w-full">Explore long-term options</Link>
            </div>
          </div>

          <div className="soft-pad mx-auto mt-6 max-w-5xl text-center">
            <h3 className="font-sans text-xl font-extrabold">More ways to teach. More ways to be discovered.</h3>
            <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
              Educators can offer different kinds of learning experiences based on their expertise,
              availability and what learners are looking for.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { t: 'Single sessions', d: 'Offer doubt clearing, exam prep or targeted consultations.' },
                { t: 'Structured courses', d: 'Guide learners through full academic sessions or curriculum.' },
                { t: 'Workshops', d: 'Host focused workshops, bootcamps, or intensive modular learning.' },
                { t: 'Long-term mastery', d: 'Support mastery in sports, music, competitive prep, or crafts.' },
              ].map((x) => (
                <div key={x.t} className="rounded-2xl border border-slate-200 bg-cream-100 p-4 text-left">
                  <div className="text-sm font-extrabold">{x.t}</div>
                  <p className="mt-1 text-xs leading-relaxed text-slate-600">{x.d}</p>
                </div>
              ))}
            </div>
            <Link to="/register" className="btn-deep mt-6">Become a YellowDots educator</Link>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------- sample needs */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
        <SectionHead
          eyebrow="Neighbourhood discovery examples"
          title="From what you need to who’s"
          marked="nearby."
          sub="Sample discovery requests from learners seeking guidance in their immediate local communities."
        />
        <div className="grid gap-4 md:grid-cols-3">
          {REQUESTS.map((r) => (
            <div key={r.quote} className="soft-pad">
              <div className="flex items-center gap-3">
                <img src={r.avatar} alt="" className="h-11 w-11 rounded-full border-2 border-dot-400 object-cover" />
                <div>
                  <div className="text-sm font-extrabold">{r.role}</div>
                  <div className="text-xs text-slate-500">{r.location}</div>
                </div>
              </div>
              <p className="mt-4 text-base font-bold leading-snug">&ldquo;{r.quote}&rdquo;</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">{r.category}</span>
                <span className="rounded-full bg-dot-400/15 px-2.5 py-1 text-[11px] font-bold text-dot-700">{r.distance}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ----------------------------------------------------- categories */}
      <section id="categories" className="border-y border-slate-200/70 bg-white py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4">
          <SectionHead
            eyebrow="Learning categories"
            title="Whatever you want to learn, start closer to"
            marked="home."
            sub="From school subjects to passions and hobbies, find the right person to learn from in your neighbourhood."
          />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {CATEGORIES.map((c) => (
              <div key={c.title} className="group soft overflow-hidden transition hover:-translate-y-1">
                <div className="relative h-36 overflow-hidden">
                  <img src={c.image} alt={c.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <span className="absolute left-3 top-3 grid h-9 w-9 place-items-center rounded-xl bg-white/95 text-deep-900 shadow">
                    {c.icon}
                  </span>
                </div>
                <div className="p-5">
                  <div className="font-extrabold">{c.title}</div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{c.description}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {c.tags.map((t) => (
                      <span key={t} className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${c.accent}`}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center font-hand text-2xl text-dot-600">
            One song, one sport &bull; one dream &bull; find what suits you.
          </p>
        </div>
      </section>

      {/* ----------------------------------------------------------- how */}
      <section id="how" className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
        <SectionHead
          eyebrow="Simple & transparent process"
          title="How YellowDots"
          marked="Works"
          sub="A frictionless flow that takes you from an immediate learning question to a trusted local connection."
        />
        <div className="soft-pad mx-auto max-w-5xl">
          <h3 className="text-center font-sans text-xl font-extrabold">
            Finding your next learning connection is simple.
          </h3>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.t} className="relative rounded-2xl border border-slate-200 bg-cream-100 p-5 text-center">
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-deep-900 px-2.5 py-0.5 text-[10px] font-extrabold text-dot-400">
                  {i + 1}
                </span>
                <span className="mx-auto mt-2 grid h-12 w-12 place-items-center rounded-2xl bg-dot-400 text-deep-900">
                  {s.icon}
                </span>
                <div className="mt-4 font-extrabold">{s.t}</div>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/register" className="btn-dot">Register as a learner <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- privacy */}
      <section id="privacy" className="border-y border-slate-200/70 bg-cream-100 py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4">
          <SectionHead
            eyebrow="Privacy & integrity first"
            title="Learning should be local. Choice should be"
            marked="yours."
            sub="We keep discovery safe, simple, and in your control so you can focus on what matters — finding the right learning connection."
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: <MapPin size={20} />, t: 'Local & convenient', d: 'Skip long commutes — learn in person or nearby with people you can trust.' },
              { icon: <Lock size={20} />, t: 'Private & safe', d: 'Contact information is never shared without your explicit consent.' },
              { icon: <ShieldCheck size={20} />, t: 'You’re in control', d: 'Accept connection requests at your discretion with full privacy guaranteed.' },
              { icon: <Target size={20} />, t: 'Better learning', d: 'Explore educators and teaching styles until you find what suits you best.' },
            ].map((f) => (
              <div key={f.t} className="soft-pad">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-deep-900 text-dot-400">{f.icon}</span>
                <div className="mt-4 font-extrabold">{f.t}</div>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------- dark: control */}
      <section className="bg-deep-900 py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4">
          <SectionHead
            tone="dark"
            eyebrow="Full control & privacy"
            title="Your learning. Your choice. Your connection."
            sub="YellowDots keeps discovery simple while giving you control over who you connect with and when."
          />
          <div className="mx-auto grid max-w-5xl gap-4 md:grid-cols-3">
            {[
              { icon: <Eye size={20} />, t: 'Browse freely', d: 'Explore options openly and compare before you reach out to anyone.' },
              { icon: <Handshake size={20} />, t: 'Your terms', d: 'Choose who to connect with and when, entirely on your own terms.' },
              { icon: <ShieldCheck size={20} />, t: 'Consent-driven', d: 'Contact details are shared only after your mutual consent.' },
            ].map((f) => (
              <div key={f.t} className="rounded-3xl border border-white/10 bg-white/5 p-6 text-white">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-dot-400 text-deep-900">{f.icon}</span>
                <div className="mt-4 font-extrabold">{f.t}</div>
                <p className="mt-1.5 text-sm leading-relaxed text-white/65">{f.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/register" className="btn-dot">Join YellowDots <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------- educator spotlight */}
      <section id="for-educators" className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="relative mx-auto w-full max-w-md">
            <span className="pointer-events-none absolute -left-4 -top-4 h-72 w-72 rounded-full bg-dot-400/20 blur-xl" />
            <div className="relative overflow-hidden rounded-3xl border-4 border-white bg-white shadow-2xl">
              <img src={IMG.educatorPortrait} alt="Educator smiling in teaching environment"
                className="h-[450px] w-full object-cover object-center" />
              <div className="absolute right-4 top-4 rounded-xl bg-white/95 px-3 py-2 shadow">
                <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Reach</div>
                <div className="text-sm font-extrabold">Learners nearby</div>
              </div>
            </div>
          </div>
          <div>
            <Eyebrow>For educators, tutors &amp; coaches</Eyebrow>
            <h2 className="mt-5 font-sans text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
              Your expertise deserves to be <Marked>found.</Marked>
            </h2>
            <p className="mt-5 leading-relaxed text-slate-600">
              Become the trusted learning pillar in your immediate neighbourhood. From being
              invisible to being discoverable.
            </p>
            <ul className="mt-6 space-y-3 text-sm font-medium text-slate-700">
              {[
                'Direct enquiries from genuine learners in your locality',
                'Work across multiple categories in one profile',
                'Set your schedule, fees, and format with total autonomy',
                'Accept only connections you want, with full privacy',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <Check size={17} className="mt-0.5 shrink-0 text-dot-600" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/register" className="btn-dot mt-8">Register as an educator <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------ learner spotlight */}
      <section id="for-learners" className="border-y border-slate-200/70 bg-cream-100 py-14 sm:py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <Eyebrow>For learners &amp; curious minds</Eyebrow>
            <h2 className="mt-5 font-sans text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
              Your next learning connection could be just around the <Marked>corner.</Marked>
            </h2>
            <p className="mt-5 leading-relaxed text-slate-600">
              Instead of searching the entire internet, see the people who can help you learn right
              around you. Learn comfortably right around the corner.
            </p>
            <ul className="mt-6 space-y-3 text-sm font-medium text-slate-700">
              {[
                'Tutors across academics, music, sports, tech & arts',
                'Filter by neighbourhood, distance, and subject',
                'Compare profiles, expertise and offerings',
                '100% free to search and discover',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <Check size={17} className="mt-0.5 shrink-0 text-dot-600" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/register" className="btn-dot mt-8">Register as a learner <ArrowRight size={17} /></Link>
          </div>
          <div className="relative order-1 mx-auto w-full max-w-md lg:order-2">
            <span className="pointer-events-none absolute -bottom-4 -right-4 h-72 w-72 rounded-full bg-dot-400/20 blur-xl" />
            <div className="relative overflow-hidden rounded-3xl border-4 border-white bg-white shadow-2xl">
              <img src={IMG.studentPortrait} alt="Young learner with books discovering learning nearby"
                className="h-[450px] w-full object-cover object-center" />
              <div className="absolute left-4 top-4 rounded-xl bg-white/95 px-3 py-2 shadow">
                <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Discover</div>
                <div className="text-sm font-extrabold">From your neighbourhood</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------- real people */}
      <section id="why" className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
        <SectionHead
          eyebrow="The YellowDots advantage"
          title="Real people. Real learning"
          marked="possibilities."
          sub="Authentic tutors, educators, and learners right in your locality."
        />
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { icon: <MapPin size={20} />, t: 'Neighbourhood focused', d: 'Real neighbourhood connections, not an endless national directory.' },
            { icon: <UserPlus size={20} />, t: 'Free to register', d: '100% free to search and discover, with no upfront commitments.' },
            { icon: <Sparkles size={20} />, t: 'Finding the right fit', d: 'Explore different educators and teaching styles until one suits you.' },
          ].map((f) => (
            <div key={f.t} className="soft-pad">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-dot-400/15 text-dot-700">{f.icon}</span>
              <div className="mt-4 font-extrabold">{f.t}</div>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------ yellow final CTA */}
      <section className="bg-dot-400 py-14 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-deep-900/10 px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.16em] text-deep-900">
            <Spark size={11} /> Join the local learning discovery network
          </span>
          <h2 className="mt-6 font-sans text-4xl font-extrabold leading-[1.12] tracking-tight text-deep-900 sm:text-5xl">
            Ready to make learning more discoverable?
          </h2>
          <p className="mx-auto mt-5 max-w-xl leading-relaxed text-deep-900/75">
            Find the right learning connection. Or make your expertise easier to find.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/register" className="btn-deep text-base">Register as a learner</Link>
            <Link to="/register" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-deep-900/25 bg-white px-6 py-3 text-sm font-bold text-deep-900 transition hover:border-deep-900/50">
              Register as an educator
            </Link>
          </div>
          <p className="mt-9 font-hand text-2xl font-bold text-deep-900/80">
            Learn &bull; Grow &bull; Thrive
          </p>
        </div>
      </section>

      {/* ------------------------------------------------------- footer */}
      <footer className="bg-deep-900 py-14 text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-dot-400 text-deep-900">
                <Spark size={16} />
              </span>
              <span className="text-lg font-extrabold tracking-tight">YellowDots</span>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/60">
              A local learning discovery network connecting people looking to learn with tutors,
              educators, coaches, and trainers around them.
            </p>
            <p className="mt-4 font-hand text-xl text-dot-400">Learning, made discoverable.</p>
          </div>

          <div>
            <div className="text-xs font-extrabold uppercase tracking-wider text-white/45">Explore YellowDots</div>
            <ul className="mt-4 space-y-2.5 text-sm text-white/70">
              <li><a href="#discover" className="transition hover:text-dot-400">Discover locally</a></li>
              <li><a href="#categories" className="transition hover:text-dot-400">Learning categories</a></li>
              <li><a href="#how" className="transition hover:text-dot-400">How YellowDots works</a></li>
              <li><a href="#privacy" className="transition hover:text-dot-400">Privacy &amp; safety</a></li>
            </ul>
          </div>

          <div>
            <div className="text-xs font-extrabold uppercase tracking-wider text-white/45">Join YellowDots</div>
            <ul className="mt-4 space-y-2.5 text-sm text-white/70">
              <li><Link to="/register" className="transition hover:text-dot-400">Register as a learner</Link></li>
              <li><Link to="/register" className="transition hover:text-dot-400">Register as an educator</Link></li>
              <li><Link to="/login" className="transition hover:text-dot-400">Log in to YellowDots</Link></li>
              <li><Link to="/aggregator-portal/register" className="transition hover:text-dot-400">Join as Partner</Link></li>
            </ul>
          </div>
        </div>

        <div className="mx-auto mt-12 flex max-w-7xl flex-col items-center justify-between gap-3 border-t border-white/10 px-4 pt-6 text-sm text-white/45 sm:flex-row">
          <p>&copy; {new Date().getFullYear()} YellowDots</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="transition hover:text-dot-400">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="transition hover:text-dot-400">Privacy Policy</Link>
          </div>
          <p>Learners find you &bull; Communities grow &bull; Brighter futures together</p>
        </div>
      </footer>
    </div>
  )
}
